import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { Loader2, RefreshCw, ArrowRight, CheckCircle2, AlertTriangle } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { DashboardLayout } from '../components/DashboardLayout';
import { controlAPI } from '../lib/api';
import { toast } from 'sonner';

// One screen, nine areas (Fan, 2026-10-25). Every figure comes from the same
// function as the area's own page; a tile that fails says so.
const STATUS = {
  green: { dot: 'bg-green-500', label: 'On track', ring: 'border-green-200 dark:border-green-900' },
  amber: { dot: 'bg-amber-500', label: 'Watch', ring: 'border-amber-200 dark:border-amber-900' },
  red: { dot: 'bg-red-500', label: 'Act now', ring: 'border-red-300 dark:border-red-900' },
  error: { dot: 'bg-gray-500', label: 'Could not check', ring: 'border-gray-300 dark:border-gray-700' },
};

function fmt(v) {
  return typeof v === 'number' ? v.toLocaleString('en-GB', { maximumFractionDigits: 0 }) : v;
}

function Tile({ tile }) {
  const s = STATUS[tile.status] || STATUS.error;
  return (
    <Card className={`border-2 ${s.ring}`} data-testid={`control-tile-${tile.key}`}>
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between gap-2">
          <CardTitle className="text-base">{tile.label}</CardTitle>
          <span className="flex items-center gap-2 text-xs text-muted-foreground">
            <span className={`h-2.5 w-2.5 rounded-full ${s.dot}`} />
            {s.label}
          </span>
        </div>
        <p className="text-sm font-medium mt-1">{tile.headline}</p>
      </CardHeader>
      <CardContent className="pt-0">
        <ul className="text-sm text-muted-foreground space-y-1">
          {(tile.lines || []).map((l, i) => <li key={i}>{l}</li>)}
        </ul>
        <Link to={tile.link} className="inline-flex items-center gap-1 text-sm text-primary mt-3 hover:underline">
          Open <ArrowRight className="h-3.5 w-3.5" />
        </Link>
      </CardContent>
    </Card>
  );
}

export function ControlPage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const load = useCallback(async (refresh = false) => {
    try {
      if (refresh) setRefreshing(true);
      const res = await controlAPI.getSummary(refresh);
      setData(res.data);
    } catch (err) {
      toast.error('Could not load the control summary');
      console.error(err);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => { load(false); }, [load]);

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
        </div>
      </DashboardLayout>
    );
  }

  const tiles = data?.tiles || [];
  const checks = data?.cross_checks || [];
  const counts = data?.counts || {};

  return (
    <DashboardLayout>
      <div className="flex flex-col gap-6">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold tracking-tight">Control</h1>
            <p className="text-muted-foreground mt-1">
              {counts.red || 0} to act on, {counts.amber || 0} to watch, {counts.green || 0} on track
              {data?.generated_at && ` · checked ${new Date(data.generated_at).toLocaleString('en-GB')}`}
            </p>
          </div>
          <Button variant="outline" size="sm" onClick={() => load(true)} disabled={refreshing}>
            {refreshing ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <RefreshCw className="h-4 w-4 mr-2" />}
            Check again
          </Button>
        </div>

        <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
          {tiles.map((t) => <Tile key={t.key} tile={t} />)}
        </div>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base">Do the pages agree?</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            {checks.map((c) => (
              <div key={c.name} className="text-sm" data-testid={`control-check-${c.name}`}>
                <div className="flex items-center gap-2 font-medium">
                  {c.ok
                    ? <CheckCircle2 className="h-4 w-4 text-green-600" />
                    : <AlertTriangle className="h-4 w-4 text-red-600" />}
                  {c.name}: {c.ok ? 'same figure everywhere' : 'pages disagree'}
                </div>
                <div className="text-muted-foreground ml-6">
                  {Object.entries(c.values || {}).map(([k, v]) => `${k} ${fmt(v)}`).join(' · ')}
                </div>
              </div>
            ))}
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  );
}

export default ControlPage;
